import {
  B2BPlanType,
  B2CPlanType,
  JoinedPlanType,
  PlanCategory,
} from "@/pages/billing/pricing-data";
import { PaymentIntervalWithoutCustom } from "@/stores/downgrade-store";

export enum PaymentInterval {
  MONTHLY = "Monthly",
  QUARTERLY = "Quarterly",
  SEMI_YEARLY = "Semi-Annual",
  ANNUAL = "Annual",
  CUSTOM = "Custom",
}

export enum SubscriptionPlan {
  FREE = "FREE",
  BASIC = "BASIC",
  PRO = "PRO",
  ENTERPRISE = "ENTERPRISE",
}

export enum SubscriptionStatus {
  ACTIVE = "ACTIVE",
  CANCELLED = "CANCELLED",
  EXPIRED = "EXPIRED",
  PENDING = "PENDING",
}

interface BillingInfo {
  amount: number;
  currency: string;
  interval: "monthly" | "yearly";
}

export interface ISubscription {
  companyId: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  credits: number;
  billing: BillingInfo;
  autoRenew: boolean;
  lastBillingDate?: string;
  nextBillingDate?: string;
  cancellationDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IAssignedMailbox {
  _id: string;
  mailboxId: string;
  assignedAt: string;
}

export interface ISeedDistribution {
  googleWorkspace: number;
  gmail: number;
  microsoft365: number;
  outlook: number;
  yahoo: number;
}

export interface IAddonPlan {
  _id: string;
  name: string;
  description: string;
  createdBy: string;
  maxEmailsPerDay: number;
  initialLimit: number;
  dailyIncrease: number;
  daysToMaxLimit: number;
  isActive: boolean;
  stripePriceId: string;
  price: number;
  seedDistribution: ISeedDistribution;
}

export interface IPendingDowngradeRequest {
  _id: string;
  type: "plan" | "billing-cycle";
  applyAt: string;
  newPlanType?: B2BPlanType | B2CPlanType;
  newInterval?: PaymentIntervalWithoutCustom;
  quantity: number;
}

export interface IPendingCancellationRequest {
  _id: string;
  applyAt: string;
  quantity: number;
}

export interface IBasePlan {
  _id: string;
  quantity: number;
  assignedMailboxes: IAssignedMailbox[];

  // max limits
  maxWarmupEmailsDaily: number;
  maxWarmupEmailsMonthly: number;
  maxHealthTestsMonthly: number;
  maxInboxPlacementTestsMonthly: number;
  maxSenderProfilesMonthly: number;

  // usage limits
  usedWarmupEmailsDaily: number;
  usedWarmupEmailsMonthly: number;
  usedHealthTestsMonthly: number;
  usedInboxPlacementTestsMonthly: number;
  usedSenderProfilesMonthly: number;

  // pending requests
  pendingRequests: {
    downgrade: IPendingDowngradeRequest | null;
    cancellation: IPendingCancellationRequest | null;
  };

  // Sender profile plan: optional seed distribution per ESP (from API)
  espTypes?: string[];
  seedCountPerSender?: number;
  seedVolumeByEspType?: Record<string, number>;
}

export interface ISimplePurchasePlan extends IBasePlan {
  planId: IPlanType;
  addonPlanId: null;
  isCustomPlan: false;
}

export interface ICustomPurchasePlan extends IBasePlan {
  planId: null;
  addonPlanId: IAddonPlan;
  isCustomPlan: true;
}

export type IPurchasePlan = ISimplePurchasePlan | ICustomPurchasePlan;

// NOTE: new one
export interface IActiveSubscriptionPlan {
  _id: string;
  companyId: string;
  interval: PaymentInterval;
  stripeSubscriptionId?: string;
  plans: IPurchasePlan[];
  startDate: string;
  endDate: string;
  status: SubscriptionStatus;
  autoRenew: boolean;
  isLatestInvoicePaid: boolean;
  lastBillingDate?: string;
  nextBillingDate?: string;
}

export type PlanVariant = {
  interval: PaymentInterval;
  stripePriceId: string;
};
export interface IPlanType {
  _id: string;
  category: PlanCategory;
  planName: string;
  planType: B2BPlanType | B2CPlanType;
  variants: PlanVariant[];
  mailboxes: number;
  senderProfiles: number;
  dailyWarmupEmails: number;
  monthlyWarmupEmails: number;
  monthlyEmailHealthTests: number;
  monthlyInboxPlacementTests: number;
  customEmailTemplates: boolean;
  warmupLanguageChoice: boolean;
  googlePostmasterIntegration: boolean;
  emailTemplateCoach: boolean;
  liveChat: boolean;
  dedicatedAccountManager: boolean;
  pricePerDomain: number;
  // Optional properties for sender profile plans
  isOnDemandSenderProfile?: boolean;
  espTypes?: ESPType[];
  seedCountPerSender?: number;
  senderProfileMetadata?: {
    productKey: string;
    espTypes: ESPType[];
    baseSeedCount: number;
    pricePerSender: number;
  };
}

export interface IPurchasePlanPayload {
  interval: PaymentInterval;
  planType: JoinedPlanType;
  quantity: number;
}

export interface ICreateCheckoutAddonPayload {
  addonId: string;
  quantity: number;
}

export interface ICheckoutSessionResponse {
  sessionId: string;
  url: string | null;
}

export interface ICustomerPortalSessionResponse {
  sessionId: string;
  url: string | null;
}

export interface IPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface IBillingDetails {
  email: string | null;
  phone?: string | null;
  name?: string | null;
  address?: {
    city: string | null;
    country: string | null;
    line1: string | null;
    line2: string | null;
    postal_code: string | null;
    state: string | null;
  } | null;
}

export interface IPaymentMethodResponse {
  defaultPaymentMethodId: string | null;
  billingDetails: IBillingDetails;
  paymentMethods: IPaymentMethod[];
}

export type InvoiceStatus =
  | "draft"
  | "open"
  | "paid"
  | "uncollectible"
  | "void";

export interface IInvoice {
  id: string;
  reference: string | null;
  amount: number;
  status: InvoiceStatus;
  createdAt: number;
  dueDate: number | null;
  invoicePdf: string | null | undefined;
  hostedInvoiceUrl: string | null | undefined;
}

export interface IInvoiceResponse {
  invoices: IInvoice[];
  hasMore: boolean;
  previousCursor: string | null;
  nextCursor: string | null;
}

export interface IUsageLimits {
  warmupEmailsSentToday: number;
  warmupEmailsSentThisMonth: number;
  healthTestsUsedThisMonth: number;
  inboxPlacementTestsUsedThisMonth: number;
  senderProfilesUsed: number;
}

export interface IUsageCredits {
  emailHealthCredits: number;
  inboxPlacementCredits: number;
  senderProfileCredits: number;
}

export interface IUsageResponse extends IUsageLimits, IUsageCredits {
  _id: string;
  companyId: string;
  credits: number;

  // max limits
  maxWarmupEmailsSentToday: number;
  maxWarmupEmailsSentThisMonth: number;
  maxHealthTestsUsedThisMonth: number;
  maxInboxPlacementTestsUsedThisMonth: number;
  maxSenderProfilesUsed: number;
  createdAt: string;
  updatedAt: string;
}
export type IUsageResponseWithNull = IUsageResponse | null;

export interface IBuyNewPlanResponse {
  message: string;
  subscription: IActiveSubscriptionPlan;
}

export interface IUpgradePlanPayload {
  subscriptionId: string;
  currentPlanId: string;
  upgradeType: "upgrade-billing-cycle" | "upgrade-plan";
  newPlanType?: B2BPlanType | B2CPlanType;
  newInterval?: PaymentInterval;
  quantity: number;
  assignedMailboxIds?: string[];
}

export interface IUpgradePlanResponse {
  message: string;
  subscription: IActiveSubscriptionPlan;
}

export interface IPendingCancellationResponse {
  _id: string;
  companyId: string;
  status: "pending" | "resolved" | "failed";
  error?: string;
  cancellationReason: string;
  serviceRating: number;
  valuableFeatures: {
    emailWarmup: boolean;
    inboxPlacementTests: boolean;
    googlePostmasterIntegration: boolean;
    emailHealthTests: boolean;
    customTemplates: boolean;
    emailTemplateCoach: boolean;
  };
  improvementSuggestions?: string;
  returnLikelihood?: string;
  subscriptionId: string;
  planId: string | null;
  addonPlanId: string | null;
  quantity: number;
  planName: string;
  assignedMailboxIds?: string[];
  applyAt: string; // ISO date string
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
}

export type UpdatePlanDtoType = {
  subscriptionId: string;
  currentPlanId: string;
  changeType: "billing-cycle" | "plan";
  newPlanType?: B2BPlanType | B2CPlanType;
  newInterval?:
    | PaymentInterval.ANNUAL
    | PaymentInterval.MONTHLY
    | PaymentInterval.QUARTERLY
    | PaymentInterval.SEMI_YEARLY;
  quantity: number;
  assignedMailboxIds?: string[];
};

export type DowngradePlanDtoType = UpdatePlanDtoType & {
  additionalFeedback?: string;
  reasons: string[];
};

export enum TestAddonType {
  EMAIL_HEALTH_TEST = "EMAIL_HEALTH_TEST",
  INBOX_PLACEMENT_TEST = "INBOX_PLACEMENT_TEST",
}

export interface IAddonTestPricing {
  _id: string;
  type: TestAddonType;
  minQuantity: number;
  maxQuantity: number | null;
  unitAmountCents: number;
  unitAmountDollars: number;
  createdAt: string;
  updatedAt: string;
}

export interface IValuableFeatures {
  emailWarmup: boolean;
  inboxPlacementTests: boolean;
  googlePostmasterIntegration: boolean;
  emailHealthTests: boolean;
  customTemplates: boolean;
  emailTemplateCoach: boolean;
}

export enum ReturnLikelihood {
  YES_DEFINITELY = "Yes, definitely",
  MAYBE = "Maybe, if improvements are made",
  NO = "No, unlikely",
}

export interface ICustomPlanRequestPayload {
  warmupsPerDay: number;
  mailboxes: number;
  inboxPlacementTestsPerMonth: number;
  emailHealthTestsPerMonth: number;
  notes?: string;
}

export interface IPlanCancellationRequestPayload {
  subscriptionId: string;
  planId?: string;
  addonPlanId?: string;
  quantity: number;
  assignedMailboxIds?: string[];

  feedback: {
    cancellationReason: {
      type: string;
      details: Record<string, string>;
    };
    serviceRating: number;
    valuableFeatures: IValuableFeatures;
    improvementSuggestions: string;
    returnLikelihood: ReturnLikelihood;
  };
}

export type ESPType = "Gmail" | "Outlook" | "Yahoo" | "Google Workspace" | "Microsoft 365" | "AOL" | "iCloud";

export interface ISenderProfileLineItem {
  senderCount: number;
  seedCountPerSender: number;
  espTypes: ESPType[];
}

export interface ICreateSenderProfileSubscriptionPayload {
  interval: PaymentInterval;
  lineItems: ISenderProfileLineItem[];
  startDate?: string; // ISO 8601 format
}

export interface ISenderProfilePlanDetails {
  _id: string;
  category: string;
  planName: string;
  planType: string;
  mailboxes: number;
  senderProfiles: number;
  dailyWarmupEmails: number;
  monthlyWarmupEmails: number;
  monthlyEmailHealthTests: number;
  monthlyInboxPlacementTests: number;
  customEmailTemplates: boolean;
  warmupLanguageChoice: boolean;
  googlePostmasterIntegration: boolean;
  emailTemplateCoach: boolean;
  liveChat: boolean;
  dedicatedAccountManager: boolean;
  pricePerDomain: number;
  isCustomPlan: boolean;
  isOnDemandSenderProfile: boolean;
  espTypes: ESPType[];
  seedCountPerSender: number;
  senderProfileMetadata: {
    productKey: string;
    espTypes: ESPType[];
    baseSeedCount: number;
    pricePerSender: number;
  };
}

export interface ISenderProfilePlan {
  planId: string;
  subscriptionId: string;
  quantity: number;
  seedCountPerSender: number;
  espTypes: ESPType[];
  maxSenderProfilesMonthly: number;
  usedSenderProfilesMonthly: number;
  stripeSubscriptionItemId: string;
  interval: PaymentInterval;
  startDate: string;
  endDate: string;
  status: SubscriptionStatus;
  planDetails: ISenderProfilePlanDetails;
}

export interface ISenderProfilePlansResponse {
  success: boolean;
  plans: ISenderProfilePlan[];
  totalPlans: number;
}

export interface IPlanDetailResponse {
  planId: string;
  quantity: number;
  isCustomPlan: boolean;
  assignedMailboxes: string[];
  maxHealthTestsMonthly: number;
  maxInboxPlacementTestsMonthly: number;
  maxSenderProfilesMonthly: number;
  maxWarmupEmailsDaily: number;
  maxWarmupEmailsMonthly: number;
  usedWarmupEmailsDaily: number;
  usedWarmupEmailsMonthly: number;
  usedHealthTestsMonthly: number;
  usedInboxPlacementTestsMonthly: number;
  usedSenderProfilesMonthly: number;
  stripeSubscriptionItemId: string;
  espTypes: ESPType[];
  seedCountPerSender: number;
  subscriptionEndDate?: string;
}

export interface ICancelSenderProfileSubscriptionPayload {
  planId: string;
}